import _ from 'lodash';
import { Filters, SortingMethod } from '@common/types';
import {
  ACTIONS,
  useTemplatesContext,
} from '@components/Contexts/TemplatesContext';
import { useInfiniteQuery } from '@tanstack/react-query';
import { queryKeyFrom } from '@utils/queryUtils';
import { TemplateQueryResponse } from '@common/types';
import {
  useErrorMonitor,
  useHttpClient,
  useTranslation,
} from '@wix/fe-essentials-standalone';
import { ENGLISH_LANGUAGE_CODE } from '@utils/constants';

export const useInfiniteTemplatesQuery = (
  filters: Partial<Filters>,
  sort: SortingMethod,
) => {
  const httpClient = useHttpClient();
  const errorMonitor = useErrorMonitor();
  const { dispatch } = useTemplatesContext();
  const {
    i18n: { language },
  } = useTranslation();
  const shouldTranslateSearchTerm = language !== ENGLISH_LANGUAGE_CODE;

  return useInfiniteQuery({
    queryKey: queryKeyFrom(filters, 1, sort, true),
    queryFn: async ({
      pageParam = 1,
    }): Promise<TemplateQueryResponse | undefined> => {
      dispatch({
        type: ACTIONS.LOADING,
      });

      try {
        const { data } = await httpClient.post<TemplateQueryResponse>(
          '/api/templates',
          {
            ...filters,
            page: pageParam,
            sort,
            shouldTranslateSearchTerm,
          },
        );
        dispatch({
          type: ACTIONS.INITIALIZED,
        });
        return data;
      } catch (err) {
        errorMonitor.captureException(err as Error);

        dispatch({
          type: ACTIONS.ERROR,
        });
      }
    },
    getNextPageParam: (
      lastPage: TemplateQueryResponse | undefined,
      allPages,
    ) => {
      return lastPage?.hasNextPage ? allPages.length + 1 : undefined;
    },
    initialPageParam: 1,
  });
};
